import React, {useMemo, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {Button} from '@albert-io/atomic';

import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';
import sessionStore from 'client/Session/SessionStore';
import PracticeView from 'components/PracticeView/PracticeView.react';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import QuestionsListPaginator from 'components/PracticeView/QuestionsListPaginator/QuestionsListPaginator.react';
import QuestionsListHeader from 'components/PracticeView/QuestionsListHeader/QuestionsListHeader.react';
import {pluralize} from 'lib/stringUtils';
import LoadingView from 'components/PracticeView/LoadingView/LoadingView.react';
import QuestionActions, {
  loadGuess
} from 'components/PracticeView/QuestionActions/QuestionActions.react';
import {
  makeGuessResultManager,
  getGuessesFromAssignment
} from 'components/PracticeView/helpers/guessResultManager';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import {
  getPreviousQuestionState,
  getNextQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';
import makeTimeElapsedManager from 'components/PracticeView/helpers/makeTimeElapsedManager';
import {queryBuilderPropType} from '@albert-io/json-api-framework/request/builder';
import {destroyQuestionStoresForQuestionSets} from 'client/components/QuestionTypes/questionTypes.utils';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import AssignmentErrorInterstitial, {
  StudentErrorComponent
} from 'client/Assignments/AssignmentErrorInterstitial.react';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {stats} from 'lib/StatsUtil';

import {
  correctAnswerSettings,
  getStudentAssignmentData,
  getInitialSetAndQuestion,
  isOutOfTime,
  isAnswerable,
  isPastDue,
  AssignmentContext,
  statusTypes,
  isGraded as isAssignmentGraded
} from './assignmentPracticeView.utils';
import {getAssignmentQuery, getAssignmentQuestionSetsQuery} from './assignmentPracticeView.queries';
import AssignmentDetails from './AssignmentDetails/AssignmentDetails.react';
import ConfirmLeaveUnfinishedAssignment from './ConfirmLeaveUnfinishedAssignment/ConfirmLeaveUnfinishedAssignment.react';
import InProgressToolbar from './InProgressToolbar/InProgressToolbar.react';

class BootstrappedAssignmentPracticeView extends React.Component {
  static propTypes = {
    questionSets: ImmutablePropTypes.list,
    params: PropTypes.shape({
      assignmentId: PropTypes.string
    }),
    route: PropTypes.object,
    router: PropTypes.object,
    questionSetsQuery: queryBuilderPropType,
    invalidateAssignment: PropTypes.func
  };

  constructor(props, context) {
    super(props, context);
    const {
      activeQuestionSet,
      activeQuestion,
      activeGuess,
      guessResults,
      correctGuessCount,
      totalGuessCount,
      paginatedQuestionsListManager
    } = this.getBootstrapValues();
    this.state = {
      guessResults,
      isViewingAssignmentDetails: true,
      activeQuestionSet,
      activeQuestion,
      activeGuess,
      correctGuessCount,
      totalGuessCount
    };
    this.paginatedQuestionsListManager = paginatedQuestionsListManager;
    /**
     * We don't initialize with a question because we don't want to start tracking until
     * the user has navigated away from the assignment details
     */
    this.timeElapsedManager = makeTimeElapsedManager();
    this.questionPositionMap = {hashCode: null, map: null};
    this.scrollToQuestionById = React.createRef();
  }

  componentDidUpdate() {
    this.timeElapsedManager.handleQuestionChange(
      this.state.isViewingAssignmentDetails ? null : this.state.activeQuestion
    );
  }

  componentWillUnmount() {
    // TODO: save draft for current question before store is destroyed
    destroyQuestionStoresForQuestionSets(this.props.questionSets);
  }

  static contextType = AssignmentContext;

  getBootstrapValues() {
    const {assignment, isGraded} = this.context;
    const {questionSets, questionSetsQuery} = this.props;
    const {questionSet, question} = getInitialSetAndQuestion({
      questionSets,
      guesses: assignment.getGuesses(),
      isGraded
    });
    const initialGuesses = getGuessesFromAssignment(assignment);
    const guessResults = makeGuessResultManager(initialGuesses);
    const guess = this.getGuessForQuestion(question, guessResults);
    if (guess) {
      const questionStoreName = question.getStore().getName();
      loadGuess(questionStoreName, guess);
    }
    const correctGuessCount = assignment.getMeta().getStudentCountOfCorrectGuesses();
    const totalGuessCount = assignment.getMeta().getStudentCountOfGuesses();
    return {
      activeQuestionSet: questionSet,
      activeQuestion: question,
      activeGuess: guess,
      guessResults,
      correctGuessCount,
      totalGuessCount,
      paginatedQuestionsListManager: new PaginatedResultsManager({
        initialQuery: questionSetsQuery,
        onUpdate: (incomingQuestionSets) => {
          if (!incomingQuestionSets) {
            this.forceUpdate();
          }
        }
      })
    };
  }

  getQuestionActions() {
    const {activeQuestion} = this.state;
    if (!activeQuestion) {
      return null;
    }
    let key = 0;
    const actions = [];
    const hasGuess = !!this.getGuessForQuestion(activeQuestion);

    if (hasGuess) {
      actions.push(
        <Button type='button' onClick={this.handleNavigateNext} variant='outlined'>
          Next Question
        </Button>
      );
    } else {
      actions.push(<QuestionActions.Next key={(key += 1)} onClick={this.handleNavigateNext} />);
    }

    actions.push(
      <QuestionActions.SubmitAnswer
        disabled={hasGuess}
        key={(key += 1)}
        assignmentId={this.props.params.assignmentId}
        onSuccess={this.handleGuessSubmission}
        getTimeElapsed={() => this.timeElapsedManager.getTimeElapsed(activeQuestion)}
      />
    );

    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  }

  handleDismissDetails = () => {
    this.setState({
      isViewingAssignmentDetails: false
    });
  };

  handleGuessSubmission = (guess) => {
    const {assignment, classroomId, studentId} = this.context;
    const question = guess.getQuestion();
    const questionId = question.getId();
    const questionSetId = question.getQuestionSet().getId();

    this.paginatedQuestionsListManager.updateQuestionForQuestionSetId({
      questionSetId,
      question
    });

    const isCorrect = guess.isCorrect();

    this.setState(
      (state) => ({
        activeQuestion: question,
        activeGuess: guess,
        guessResults: state.guessResults.updateGuess(questionId, guess, true),
        correctGuessCount: state.correctGuessCount + (isCorrect ? 1 : 0),
        totalGuessCount: state.totalGuessCount + 1
      }),
      () => {
        const isLastGuessSubmission =
          this.state.guessResults.getGuessResultsMap().size ===
          assignment.getMeta().getCountOfQuestions();
        if (isLastGuessSubmission) {
          this.props.invalidateAssignment();
        } else if (
          assignment.getCorrectAnswerSettingFromSettings({classroomId, studentId}) !==
          correctAnswerSettings.showAfterEach
        ) {
          this.handleNavigateNext();
        }
      }
    );
  };

  canNavigateBack = () => {
    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets: this.paginatedQuestionsListManager.getResults(),
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });

    const {question} = previousSetAndQuestion || {};

    return !!question;
  };

  handleNavigateBack = () => {
    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets: this.paginatedQuestionsListManager.getResults(),
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });

    const {question, questionSet} = previousSetAndQuestion || {};

    if (question) {
      this.loadQuestion({
        question,
        questionSet
      });
      this.scrollToQuestionById.current(questionSet.getId());
    }
  };

  canNavigateNext = () => {
    const nextSetAndQuestion = getNextQuestionState({
      questionSets: this.paginatedQuestionsListManager.getResults(),
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });

    const {question} = nextSetAndQuestion || {};

    return !!question;
  };

  handleNavigateNext = () => {
    const nextSetAndQuestion = getNextQuestionState({
      questionSets: this.paginatedQuestionsListManager.getResults(),
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });

    const {question, questionSet} = nextSetAndQuestion || {};

    if (question) {
      this.loadQuestion({
        question,
        questionSet
      });
      this.scrollToQuestionById.current(questionSet.getId());
    } else {
      // if no question, we assume it's the last question in the assignment, and return to first question (see https://github.com/albert-io/project-management/issues/6486)
      const firstSetOnPage = this.paginatedQuestionsListManager.getQuestionSetsInPage(1).first();

      const firstQuestion = firstSetOnPage.getQuestions().first();
      this.loadQuestion({
        question: firstQuestion,
        questionSet: firstSetOnPage
      }).then(() => {
        this.scrollToQuestionById.current(firstSetOnPage.getId());
      });
    }
  };

  navigateAndScrollToPage(pageNum) {
    const firstSetOnPage = this.paginatedQuestionsListManager
      .getQuestionSetsInPage(pageNum)
      .first();
    const firstQuestion = firstSetOnPage.getQuestions().first();
    this.loadQuestion({
      question: firstQuestion,
      questionSet: firstSetOnPage
    }).then(() => {
      this.scrollToQuestionById.current(firstSetOnPage.getId());
    });
  }

  handlePaginatorPreviousClick = async () => {
    const previousPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) - 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(previousPage)) {
      await this.paginatedQuestionsListManager.fetchPreviousPage();
    }
    this.navigateAndScrollToPage(previousPage);
  };

  handlePaginatorNextClick = async () => {
    const nextPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) + 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(nextPage)) {
      await this.paginatedQuestionsListManager.fetchNextPage();
    }
    this.navigateAndScrollToPage(nextPage);
  };

  handleQuestionClick = ({question, questionSet}) => {
    return () => {
      this.loadQuestion({
        question,
        questionSet
      });
    };
  };

  loadQuestion = async ({question, questionSet}) => {
    const guess = this.getGuessForQuestion(question);
    if (guess) {
      const questionStoreName = question.getStore().getName();
      loadGuess(questionStoreName, guess);
    }
    return new Promise((res) => {
      this.setState(() => {
        return {
          activeQuestion: question,
          activeQuestionSet: questionSet,
          activeGuess: guess
        };
      }, res);
    });
  };

  getGuessForQuestion(question, guessResults = this.state.guessResults) {
    const questionId = question?.getId?.();
    const {isGraded} = this.context;
    return (
      guessResults.getGuess(questionId) ||
      (isGraded ? question.getStore().buildEmptyGuess(question) : null)
    );
  }

  areExplanationsAvaialable() {
    const {classroomId, studentId, isGraded, assignment} = this.context;
    return (
      isGraded ||
      assignment.getCorrectAnswerSettingFromSettings({classroomId, studentId}) ===
        correctAnswerSettings.showAfterEach
    );
  }

  getQuestionPosition({questionSets, questionId}) {
    const hashCode = questionSets.hashCode();
    if (this.questionPositionMap.hashCode !== hashCode) {
      this.questionPositionMap.hashCode = hashCode;
      this.questionPositionMap.map = questionSets.reduce(
        (acc, questionSet, i, list) => {
          questionSet.getQuestions().forEach((question) => {
            const pos = acc[1] + 1;
            acc[0][question.getId()] = pos;
            acc[1] = pos;
          });
          if (list.size === i + 1) {
            return acc[0];
          }
          return acc;
        },
        [{}, 0]
      );
    }

    return this.questionPositionMap.map[questionId];
  }

  render() {
    const {router, route, invalidateAssignment} = this.props;
    const {assignment, classroom, isGraded, status} = this.context;
    const answeredQuestionCount = this.state.guessResults.getGuessResultsMap().size;
    const questionCount = assignment.getMeta().getCountOfQuestions();
    const {
      activeQuestion,
      activeQuestionSet,
      activeGuess,
      isViewingAssignmentDetails,
      guessResults,
      correctGuessCount,
      totalGuessCount
    } = this.state;
    const accuracy = stats.percentage(correctGuessCount, totalGuessCount);
    const subject = activeQuestionSet ? activeQuestionSet.getSubject() : null;
    const currentPage = this.paginatedQuestionsListManager.getPageForId(
      activeQuestionSet?.getId?.()
    );

    const questionSets = this.paginatedQuestionsListManager.getResults();

    const hasPreviousHistoryEntry = (global.history?.length || 0) > 1;

    /**
     * This should be a setting on the assignment. For now, we'll display titles and difficulty.
     * Once it's a setting, change this to `!isGraded && assignment.getWhateverThatSettingValueIs()`
     */
    const shouldObfuscateTitleAndDifficulty = false;

    const areExplanationsAvaialable = this.areExplanationsAvaialable();

    return (
      <MobilePracticeViewProvider>
        <PracticeView
          activeQuestion={activeQuestion}
          activeQuestionSet={activeQuestionSet}
          activeQuestionStore={activeQuestion && activeQuestion.getStore()}
          activeGuess={activeGuess}
          activeSubject={subject}
          questionSets={questionSets}
          showDifficulty={!shouldObfuscateTitleAndDifficulty}
          titleBar={
            <TitleBar
              title={assignment.getName()}
              backButton={
                <TitleBar.BackButton
                  as={isViewingAssignmentDetails ? Link : 'button'}
                  label='Back To Classroom'
                  onClick={
                    isViewingAssignmentDetails
                      ? () => {
                          if (hasPreviousHistoryEntry) {
                            router.goBack();
                          }
                        }
                      : () =>
                          this.setState({
                            isViewingAssignmentDetails: true
                          })
                  }
                  to={
                    isViewingAssignmentDetails && hasPreviousHistoryEntry
                      ? undefined
                      : `/classes/${classroom.getId()}/my-assignments`
                  }
                />
              }
            />
          }
          toolbar={
            !isViewingAssignmentDetails &&
            !isGraded && (
              <InProgressToolbar
                invalidateAssignment={invalidateAssignment}
                answeredQuestionCount={answeredQuestionCount}
                questionCount={questionCount}
                accuracy={areExplanationsAvaialable ? accuracy : null}
              />
            )
          }
          questionProps={{
            onQuestionClick: this.handleQuestionClick,
            attemptIndicatorValue: ({question}) => {
              const result = guessResults.getGuessResult(question.getId());
              return areExplanationsAvaialable ? result : null;
            },
            strikethroughQuestionInfo: ({questionSet, question}) => {
              if (isGraded) {
                return false;
              }
              return question
                ? !!guessResults.getGuessResult(question.getId())
                : questionSet
                    .getQuestions()
                    .every((qsQuestion) => !!guessResults.getGuessResult(qsQuestion.getId()));
            },
            modifyTitle: ({questionSet, question}) => {
              if (!question) {
                return shouldObfuscateTitleAndDifficulty ? 'Question Set' : questionSet.getName();
              }

              const position = this.getQuestionPosition({
                questionSets,
                questionId: question.getId()
              });

              const title = shouldObfuscateTitleAndDifficulty
                ? 'Question'
                : question?.getTitle() || questionSet.getName();

              // We're adding a zero-width space before the position so that this doesn't render
              // as an ordered list 👇
              return shouldObfuscateTitleAndDifficulty
                ? `Question ${position}`
                : `&#8291;${position}. ${title}`;
            }
          }}
          questionsListProps={{
            onScrollLastQuestionIntoView: () => {
              if (!this.paginatedQuestionsListManager.isRequestInFlight) {
                this.paginatedQuestionsListManager.fetchNextPage();
              }
            },
            scrollToQuestionById: this.scrollToQuestionById,
            header: (
              <QuestionsListHeader
                heading={`${questionCount} ${pluralize('Question', questionCount)}`}
                filterable={false}
                sortable={false}
              />
            ),
            footer: (
              <QuestionsListPaginator
                currentPage={currentPage}
                isRequestInFlight={this.paginatedQuestionsListManager.isRequestInFlight}
                totalPages={this.paginatedQuestionsListManager.totalPages}
                isPreviousDisabled={
                  this.paginatedQuestionsListManager.isRequestInFlight || currentPage === 1
                }
                handlePreviousPage={this.handlePaginatorPreviousClick}
                isNextDisabled={
                  this.paginatedQuestionsListManager.isRequestInFlight ||
                  currentPage === this.paginatedQuestionsListManager.totalPages
                }
                handleNextPage={this.handlePaginatorNextClick}
              />
            )
          }}
          questionRendererProps={{
            actions: this.getQuestionActions(),
            rendererType:
              this.areExplanationsAvaialable() && activeGuess ? 'PostGuess' : 'PreGuess',
            interactive: !activeGuess,
            modifyTitle: !shouldObfuscateTitleAndDifficulty
              ? null
              : ({question}) => {
                  const position = this.getQuestionPosition({
                    questionSets,
                    questionId: question.getId()
                  });
                  return `Question ${position}`;
                },
            navigateBackProps: {
              disabled: !this.canNavigateBack(),
              handleNavigation: this.handleNavigateBack
            },
            navigateNextProps: {
              disabled: !this.canNavigateNext(),
              handleNavigation: this.handleNavigateNext
            }
          }}
          stageComponent={
            isViewingAssignmentDetails && (
              <AssignmentDetails
                onDismissDetails={this.handleDismissDetails}
                invalidateAssignment={invalidateAssignment}
              />
            )
          }
          hasQuestionSetAccessPredicate={(questionSet) => {
            if (questionSet.getMeta().isFree()) {
              return true;
            }
            const subjectMeta = questionSet.getSubject().getMeta();
            return (
              subjectMeta.hasAccessViaLicenseAsStudent() || subjectMeta.hasAccessViaSubscription()
            );
          }}
        />
        {status === statusTypes.inProgress && !isGraded && (
          <ConfirmLeaveUnfinishedAssignment route={route} onDismiss={this.handleDismissDetails} />
        )}
      </MobilePracticeViewProvider>
    );
  }
}

const AssignmentPracticeView = (props) => {
  const studentId = sessionStore.getUserId();
  const queryArgs = {
    assignmentId: props.params.assignmentId,
    studentId
  };
  const assignmentQuery = getAssignmentQuery(queryArgs);
  const getQuestionSetsQuery = () => {
    const resolvedAssignment = assignmentQuery.getResource();
    const {classroom} =
      getStudentAssignmentData({
        assignment: resolvedAssignment
      }) || {};
    const randomize =
      resolvedAssignment.isRandomizeQuestionOrder() &&
      !isAssignmentGraded({
        assignment: resolvedAssignment,
        classroomId: classroom.getId(),
        studentId
      });
    return getAssignmentQuestionSetsQuery({...queryArgs, randomize});
  };

  const {isReady, invalidateQueries} = useAreLatestQueriesReady(
    [assignmentQuery, getQuestionSetsQuery],
    {
      resolveInOrder: true
    }
  );

  const assignment = assignmentQuery.getResource();
  const {status, classroom, questionCount, student, isGraded} = isReady
    ? getStudentAssignmentData({
        assignment
      })
    : {};
  const classroomId = classroom?.getId();
  const isAssignmentOutOfTime = isReady ? isOutOfTime({assignment, classroomId, studentId}) : null;
  const isAssignmentAnswerable = isReady
    ? isAnswerable({assignment, classroomId, studentId})
    : null;
  const isAssignmentPastDue = isReady ? isPastDue({assignment, classroomId, studentId}) : null;

  const contextValues = useMemo(() => {
    return {
      assignment,
      classroom,
      student,
      isGraded,
      status,
      isOutOfTime: isAssignmentOutOfTime,
      isAnswerable: isAssignmentAnswerable,
      isPastDue: isAssignmentPastDue,
      classroomId,
      studentId
    };
  }, [
    assignment,
    classroom,
    student,
    isGraded,
    status,
    isAssignmentOutOfTime,
    isAssignmentAnswerable,
    isAssignmentPastDue,
    classroomId,
    studentId
  ]);

  const invalidateAssignment = useCallback(() => {
    if (!isReady) {
      return;
    }
    const questionSetsQuery = getQuestionSetsQuery();
    const questionSetsPageCount = questionSetsQuery
      .getResourceMetadata()
      .getIn(['page', 'total_pages']);
    if (questionSetsPageCount > 1) {
      invalidatePartialInterest({resourcePath: questionSetsQuery.done().resourcePath});
    }
    invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  useEffect(() => {
    return invalidateAssignment;
  }, [invalidateAssignment]);

  if (!isReady) {
    return <LoadingView />;
  }
  const builtQuestionSetsQuery = getQuestionSetsQuery();
  return (
    <AssignmentContext.Provider value={contextValues}>
      <BootstrappedAssignmentPracticeView
        {...props}
        questionSets={builtQuestionSetsQuery.getResource()}
        questionSetsQuery={builtQuestionSetsQuery}
        questionCount={questionCount}
        invalidateAssignment={invalidateAssignment}
      />
    </AssignmentContext.Provider>
  );
};

AssignmentPracticeView.propTypes = {
  params: PropTypes.object
};

export default requireSessionWithLoginPrompt()((props) => (
  <AssignmentErrorInterstitial
    assignmentId={props.params.assignmentId}
    errorComponent={StudentErrorComponent}
  >
    <AssignmentPracticeView {...props} />
  </AssignmentErrorInterstitial>
));
